import { Component, OnInit } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { concat, of } from "rxjs";
import { catchError, map, tap, toArray } from "rxjs/operators";
import { SIGNING_AGENT_DESIGNATION_LABELS, SigningAgentDesignation } from "../../../shared/enums/signingAgentDesignation";
import { SigningAgentDesignationByEmail } from "../../../shared/models/signing-agent-designation-by-email";
import { SigningAgentDesignationService } from "../../../shared/services/signing-agent-designation.service";
import { AppSpinnerModule } from "src/app/shared/modules/app-spinner.module";

@UntilDestroy()
@Component({
    selector: 'add-signing-agent-designation-modal',
    templateUrl: './add-signing-agent-designation-modal.component.html',
    styleUrls: ['./add-signing-agent-designation-modal.component.scss'],
    standalone: false
})
export class AddSigningAgentDesignationModalComponent implements OnInit {
  addDesignationForm: UntypedFormGroup;
  addDesignationsArray: UntypedFormArray;
  private anyPersisted = false;
  designations = [
    { key: SigningAgentDesignation.TeamSigningAgent, value: SIGNING_AGENT_DESIGNATION_LABELS[SigningAgentDesignation.TeamSigningAgent] },
    { key: SigningAgentDesignation.PreferredAgent, value: SIGNING_AGENT_DESIGNATION_LABELS[SigningAgentDesignation.PreferredAgent] }
  ];

  constructor(
    private readonly activeModal: NgbActiveModal,
    private readonly designationService: SigningAgentDesignationService,
    private readonly fb: UntypedFormBuilder,
    private readonly spinner: AppSpinnerModule,
    private readonly toastr: ToastrService,
  ) {}

  ngOnInit() {
    this.addDesignationForm = new UntypedFormGroup({
      designationAccounts: this.fb.array([])
    });

    this.addDesignationsArray = this.addDesignationForm.get('designationAccounts') as UntypedFormArray;
    this.addDesignationRow();
  }

  addDesignationRow() {
    const designationAccountForm = this.fb.group({
      emailAddress: ['', [
        Validators.email,
        Validators.maxLength(200),
        Validators.required,
      ]],
      designationCode: ['', Validators.required],
    });
    this.addDesignationsArray.push(designationAccountForm);
  }

  removeDesignationRow(index: number) {
    this.addDesignationsArray.removeAt(index);
  }

  close(): void {
    this.activeModal.close(this.anyPersisted ? 'designationsAdded' : 'no designation added');
  }

  addDesignations() {
    if (this.addDesignationForm.invalid) {
      this.markFormControlsAsDirty(this.addDesignationForm);
      this.toastr.warning("Please correct all errors.", "Attention");
      return;
    }

    const duplicate = this.findDuplicateEmailAddress();
    if (duplicate) {
      this.toastr.warning(
        `${duplicate} appears on more than one row. An agent holds a single designation, so remove the duplicate row.`,
        "Attention"
      );
      return;
    }

    this.spinner.show();

    const requests = this.addDesignationsArray.controls.map(designationAccountGroup => {
      const emailAddress = designationAccountGroup.value.emailAddress;

      return this.designationService.putDesignationByEmail(
        this.createSigningAgentDesignationByEmailModel(designationAccountGroup.value)
      ).pipe(
        map(() => ({ emailAddress, error: null as string | null })),
        catchError((error) => of({
          emailAddress,
          error: error?.message ?? `Failed to add a designation for ${emailAddress}.`
        }))
      );
    });

    concat(...requests)
      .pipe(
        toArray(),
        tap(results => {
          this.spinner.hide();
          this.reportResults(results);
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private reportResults(results: { emailAddress: string; error: string | null }[]): void {
    const succeeded = results.filter(result => !result.error);
    const failed = results.filter(result => result.error);

    if (succeeded.length > 0) {
      this.anyPersisted = true;
      this.toastr.success(
        succeeded.length === 1
          ? "Designation added successfully."
          : `${succeeded.length} designations added successfully.`
      );
    }

    if (failed.length === 0) {
      this.activeModal.close('designationsAdded');
      return;
    }

    failed.forEach(result => this.toastr.error(result.error ?? undefined));
    this.removeRowsFor(succeeded.map(result => result.emailAddress));
  }

  private removeRowsFor(emailAddresses: string[]): void {
    for (let index = this.addDesignationsArray.length - 1; index >= 0; index--) {
      if (emailAddresses.includes(this.addDesignationsArray.at(index).value.emailAddress)) {
        this.addDesignationsArray.removeAt(index);
      }
    }
  }

  private findDuplicateEmailAddress(): string | null {
    const emailAddresses = this.addDesignationsArray.controls.map(
      control => (control.value.emailAddress ?? "").trim().toLowerCase()
    );

    const duplicateIndex = emailAddresses.findIndex(
      (emailAddress, index) => emailAddress && emailAddresses.indexOf(emailAddress) !== index
    );

    return duplicateIndex === -1 ? null : emailAddresses[duplicateIndex];
  }

  private createSigningAgentDesignationByEmailModel(designationAccountGroup): SigningAgentDesignationByEmail {
    const designationByEmail: SigningAgentDesignationByEmail = {
      emailAddress: designationAccountGroup.emailAddress,
      designationCode: designationAccountGroup.designationCode
    };
    return designationByEmail;
  }

  private markFormControlsAsDirty(formGroup: UntypedFormGroup | UntypedFormArray) {
    Object.values(formGroup.controls).forEach(control => {
        if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
          this.markFormControlsAsDirty(control);
        } else {
          control.markAsDirty();
        }
      });
  }
}
